import { useEffect, useState } from "react";
import { BellRing, Send, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export default function PushAlertsCard() {
  const supported = "serviceWorker" in navigator && "PushManager" in window;
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker
      .getRegistration()
      .then(async (reg) => {
        const sub = await reg?.pushManager?.getSubscription();
        setEnabled(!!sub);
      })
      .catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications were blocked — allow them in your browser settings and try again.");
        return;
      }
      const { data } = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setEnabled(true);
      toast.success("This device will now get an alert for every new inquiry!");
    } catch (e) {
      toast.error("Couldn't enable notifications on this device");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      const r = await api.post("/push/test");
      toast.success(`Test notification sent to ${r.data.sent_to} device(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  return (
    <div className="border border-soft bg-card p-6 flex flex-wrap items-center justify-between gap-4" data-testid="push-alerts-card">
      <div className="flex items-start gap-3 max-w-xl">
        <BellRing className="h-5 w-5 text-accent mt-0.5" />
        <div>
          <p className="font-serif text-xl text-primary">Inquiry alerts on this device</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Get a pop-up notification the moment a customer sends a test drive, financing, or contact inquiry — free, no phone plan needed.
          </p>
          <p className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1">
            <Smartphone className="h-3 w-3" />
            iPhone: open this page in Safari → Share → &ldquo;Add to Home Screen&rdquo; → open the app → tap Enable.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!supported ? (
          <p className="text-sm text-muted-foreground" data-testid="push-not-supported">Not supported in this browser.</p>
        ) : enabled ? (
          <>
            <span className="text-sm text-primary font-medium" data-testid="push-enabled-label">✓ Alerts on</span>
            <Button size="sm" variant="outline" onClick={sendTest} className="rounded-sm gap-1" data-testid="push-send-test">
              <Send className="h-3 w-3" /> Send test
            </Button>
          </>
        ) : (
          <Button onClick={enable} disabled={busy} className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/90" data-testid="push-enable-btn">
            {busy ? "Enabling…" : "Enable Alerts"}
          </Button>
        )}
      </div>
    </div>
  );
}
